/**
 * Client-side sequence tracking for the `/events` NDJSON stream — a pure
 * state machine with no React in it, held in a single ref by
 * `useChatMessages` and unit-tested without renders.
 *
 * The server stamps every published event with a per-session monotonic
 * `seq`, scoped by a bus-life `epoch` advertised in the `subscribed`
 * snapshot. Replay is cursor-free server-side: a reconnect re-receives
 * the in-flight ring in full, and THIS tracker is what keeps the
 * transcript correct, by answering three questions:
 *
 * 1. **Duplicate suppression** — has this frame already been applied?
 *    (`observe` returns false for a re-delivered `seq`.)
 * 2. **Gap detection** — did frames vanish for good (flushed at turn end
 *    or evicted from the ring while disconnected)? A non-contiguous seq,
 *    a snapshot whose `latestSeq` outran us across an idle boundary, or
 *    an epoch change all flag a resync.
 * 3. **Resync scheduling** — the flag is consumed at a turn boundary
 *    (`done`, `error`, or an idle snapshot), when persisted history is
 *    complete, so the caller can reseed without clobbering a live turn.
 */

/** The seq/epoch fields of the `subscribed` snapshot, pre-validation. */
export type SnapshotResumeFields = {
  currentTurn?: unknown;
  latestSeq?: unknown;
  epoch?: unknown;
};

export class ChatStreamCursor {
  private lastSeq = 0;

  private epoch: number | null = null;

  private needsResync = false;

  /**
   * Observe a frame's `seq` before applying it. Returns `false` when the
   * frame is a duplicate re-delivered by the cursor-free replay (seq at
   * or below the last applied) and must be dropped; `true` when the
   * caller should apply it. Frames without a numeric seq (the snapshot,
   * legacy/mock events) always apply. A forward jump past the next
   * contiguous seq flags a resync: those frames are gone for good.
   */
  public observe(seq: unknown): boolean {
    if (typeof seq !== 'number') return true;

    if (this.lastSeq > 0 && seq <= this.lastSeq) return false;

    if (this.lastSeq > 0 && seq > this.lastSeq + 1) {
      this.needsResync = true;
    }

    this.lastSeq = seq;

    return true;
  }

  /**
   * Reconcile with a (re)connect `subscribed` snapshot — always the
   * first frame of every stream attempt. Adopts the server's bus-life
   * epoch (a change means our seqs are from a destroyed bus: drop the
   * tracking, and flag a resync if we had applied content from that
   * life), and detects frames that were published while we were away
   * but ended with the turn — unrecoverable from the stream, so flag a
   * resync. Returns whether the server reports a turn in flight.
   */
  public reconcileSnapshot(snapshot: SnapshotResumeFields): {
    isBusy: boolean;
  } {
    const isBusy = snapshot.currentTurn === 'busy';

    if (typeof snapshot.epoch === 'number') {
      if (this.epoch !== snapshot.epoch) {
        if (this.epoch !== null && this.lastSeq > 0) {
          this.needsResync = true;
        }

        this.epoch = snapshot.epoch;
        this.lastSeq = 0;
      }
    }

    if (typeof snapshot.latestSeq === 'number') {
      if (snapshot.latestSeq < this.lastSeq) {
        // Defensive: with epoch handling above this shouldn't happen (a
        // lower counter implies a new life), but tracking that somehow
        // outran the server's counter is meaningless — drop it and
        // accept the fresh replay.
        this.lastSeq = 0;
      } else if (
        this.lastSeq > 0 &&
        snapshot.latestSeq > this.lastSeq &&
        !isBusy
      ) {
        // Frames were published after our last applied seq but the turn
        // is over — the replay buffer was flushed at its end, so they're
        // unrecoverable from the stream. (Mid-turn, the imminent replay
        // usually still covers them; the seq-jump check in `observe`
        // catches the case where it doesn't.) Jump the tracker so the
        // next reconnect doesn't re-detect the same loss and reseed
        // again.
        this.needsResync = true;
        this.lastSeq = snapshot.latestSeq;
      }
    }

    return { isBusy };
  }

  /**
   * Consume the resync flag at a turn boundary (`done`, `error`, or an
   * idle snapshot). Returns `true` exactly once per flagged loss — the
   * caller must then rebuild the transcript from persisted history.
   */
  public consumeAtTurnEnd(): boolean {
    if (!this.needsResync) return false;

    this.needsResync = false;

    return true;
  }

  /**
   * Re-arm the resync flag — used when a stale seed was refused mid-turn
   * so the next turn end reseeds with fresher rows.
   */
  public flagResync(): void {
    this.needsResync = true;
  }

  /** Clear all tracking (session switch, reset, new chat). */
  public reset(): void {
    this.lastSeq = 0;
    this.epoch = null;
    this.needsResync = false;
  }
}
